// Sincronizador de la cola (POST /ventas/sync). Uno por pestaña; un solo sync a la vez entre pestañas.
// Separado del heartbeat: el heartbeat decide modoOffline; esto decide cuándo reintentar cada venta.
import { watch } from 'vue';
import { db } from '@/db/posDb';
import { fetchWrapper } from '@/helpers/fetchWrapper';
import { SYNC, AVISOS } from '@/config/offline';
import { useAuth, useConexion } from '@/stores';
import { actualizarControl } from '@/services-offline/colaVentas';
// Etapa 4: los cierres de caja guardados en el equipo se envían al final de cada ciclo, detrás de las ventas
import { enviarCierresPendientes, proximosIntentosCierres, purgarCierres } from '@/services-offline/cierresCaja';

const API = process.env.VUE_APP_ROOT_API;
let activo = false;
let temporizador = null;
let detenerWatch = null;
let intervaloPurga = null;
let intervaloEdad = null;
let enCurso = null;
let tamLote = SYNC.loteMax;

const ahoraIso = () => new Date().toISOString();

function espera(intentos) {
  const base = Math.min(SYNC.backoffBaseMs * 2 ** Math.max(0, intentos - 1), SYNC.backoffMaxMs);
  return Math.round(base * (0.8 + Math.random() * 0.4));
}

// Candado entre pestañas: navigator.locks si existe; si no, una fila con caducidad en contadores
// Etapa 4: se exporta (el cierre de caja cuenta dentro del candado) y acepta { esperar: true }: espera a que se
// libere en lugar de devolver null (en el respaldo, como mucho SYNC.candadoMs). Antes:
// async function conCandado(fn) {
//   if (navigator.locks && navigator.locks.request) {
//     return navigator.locks.request('zapi-sync', { ifAvailable: true }, (lock) => (lock ? fn() : null));
//   }
export async function conCandado(fn, { esperar = false } = {}) {
  if (navigator.locks && navigator.locks.request) {
    if (esperar) return navigator.locks.request('zapi-sync', () => fn());
    return navigator.locks.request('zapi-sync', { ifAvailable: true }, (lock) => (lock ? fn() : null));
  }
  const tomar = () => db.transaction('rw', db.contadores, async () => {
    const lock = await db.contadores.get('__lock_sync');
    if (lock && lock.expira > Date.now()) return false;
    await db.contadores.put({ caja_id: '__lock_sync', expira: Date.now() + SYNC.candadoMs });
    return true;
  });
  // Antes: const tomado = await db.transaction('rw', db.contadores, async () => { … });
  let tomado = await tomar();
  const limite = Date.now() + SYNC.candadoMs;
  while (!tomado && esperar && Date.now() < limite) {
    await new Promise((r) => setTimeout(r, 250));
    tomado = await tomar();
  }
  if (!tomado) return null;
  try { return await fn(); } finally { await db.contadores.delete('__lock_sync'); }
}

// H10: cada venta se sincroniza con su usuario (el backend atribuye el pago al usuario del token)
async function pendientesDelUsuario() {
  const auth = useAuth();
  const userId = auth.user ? auth.user.id : null;
  const filas = await db.cola_ventas.where('estado').equals('pendiente').toArray();
  return filas.filter((v) => Number(v.user_id) === Number(userId));
}

async function listasParaEnviar(manual) {
  const ahora = Date.now();
  return (await pendientesDelUsuario())
    .filter((v) => manual || !v.proximo_intento_en || Date.parse(v.proximo_intento_en) <= ahora)
    .sort((a, b) => a.fecha_venta_cliente.localeCompare(b.fecha_venta_cliente) || a.creada_en.localeCompare(b.creada_en));
}

async function devolverAPendiente(lote, { contarIntento, ultimoError }) {
  for (const v of lote) {
    const intentos = contarIntento ? v.intentos + 1 : v.intentos;
    await actualizarControl(v.uuid_venta, {
      estado: 'pendiente', intentos, ultimo_error: ultimoError,
      proximo_intento_en: contarIntento ? new Date(Date.now() + espera(intentos)).toISOString() : v.proximo_intento_en,
    });
  }
}

// Devuelve true si hay que parar el ciclo
async function aplicarRespuesta(lote, res) {
  const conexion = useConexion();
  if (res.status === 401) {
    conexion.sesionExpirada = true; // se pausa sin perder nada; se reanuda al iniciar sesión otra vez
    await devolverAPendiente(lote, { contarIntento: false, ultimoError: 'Sesión vencida' });
    return true;
  }
  if (res.status === 403) {
    conexion.syncSinPermiso = true; // usuario sin «Venta POS»: se pausa hasta otro login
    await devolverAPendiente(lote, { contarIntento: false, ultimoError: 'Sin permiso Venta POS' });
    return true;
  }
  if (res.status === 422) {
    if (lote.length > 1) {
      tamLote = Math.ceil(lote.length / 2); // no debería pasar con lotes ≤ 50: se parte y se reintenta
      await devolverAPendiente(lote, { contarIntento: false, ultimoError: null });
      return false;
    }
    await actualizarControl(lote[0].uuid_venta, { estado: 'rechazada', ultimo_error: (res.data && res.data.message) || 'Datos inválidos' });
    tamLote = SYNC.loteMax;
    return false;
  }
  if (!res.ok || !Array.isArray(res.data)) {
    // Red, timeout o 5xx: todo el lote vuelve a pendiente con backoff
    await devolverAPendiente(lote, { contarIntento: true, ultimoError: res.status ? `HTTP ${res.status}` : 'Sin conexión' });
    return true;
  }
  const porUuid = Object.fromEntries(res.data.map((r) => [r.uuid_venta, r]));
  for (const v of lote) {
    const r = porUuid[v.uuid_venta];
    if (r && r.status === 'ok') {
      await actualizarControl(v.uuid_venta, { estado: 'sincronizada', venta_id: r.venta_id, ultimo_error: null, sincronizada_en: ahoraIso() });
    } else if (r && r.status === 'duplicada') {
      // Ya estaba en el servidor (un intento anterior entró): cuenta como enviada
      await actualizarControl(v.uuid_venta, { estado: 'sincronizada', ultimo_error: 'duplicada', sincronizada_en: ahoraIso() });
    } else if (r && r.status === 'rechazada') {
      // P. ej. caja ya cerrada: queda con su motivo y nunca se borra sola
      await actualizarControl(v.uuid_venta, { estado: 'rechazada', ultimo_error: r.mensaje || 'Rechazada por el servidor' });
    } else {
      // 'error' o sin resultado para esta venta (defensivo): pendiente con backoff
      await devolverAPendiente([v], { contarIntento: true, ultimoError: (r && r.mensaje) || 'Sin resultado del servidor' });
    }
  }
  tamLote = SYNC.loteMax;
  return false;
}

async function ciclo(manual) {
  const conexion = useConexion();
  if (!activo || conexion.modoOffline) return;
  if (!manual && (conexion.sesionExpirada || conexion.syncSinPermiso)) return;
  await conCandado(async () => {
    conexion.sincronizando = true;
    try {
      for (;;) {
        const pendientes = await listasParaEnviar(manual);
        if (!pendientes.length || !activo) break;
        const lote = pendientes.slice(0, tamLote);
        for (const v of lote) await actualizarControl(v.uuid_venta, { estado: 'enviando' });
        const res = await fetchWrapper.raw('POST', `${API}/ventas/sync`, { ventas: lote.map((v) => v.payload) }, { timeoutMs: SYNC.timeoutLoteMs });
        if (res.reloj) conexion.registrarReloj(res.reloj); // Etapa 3D (B)
        if (await aplicarRespuesta(lote, res)) break;
      }
      // Etapa 4 (d): los cierres van DETRÁS de las ventas, para que el servidor ya tenga todo lo posible.
      // Con la sesión vencida o sin red no se intenta (enviarCierre lo vuelve a programar).
      if (activo && !conexion.sesionExpirada && !conexion.modoOffline) {
        await enviarCierresPendientes(manual).catch((e) => console.warn('Envío de cierres de caja:', e));
      }
    } finally {
      conexion.sincronizando = false;
      await conexion.refrescarContadoresCola();
    }
  });
  await programarSiguiente();
}

// Solo las pendientes del usuario con sesión: las de otro usuario no se pueden enviar desde aquí
// (si contaran, el temporizador se dispararía cada segundo sin enviar nada)
async function programarSiguiente() {
  clearTimeout(temporizador);
  temporizador = null;
  if (!activo) return;
  const proximos = (await pendientesDelUsuario())
    .map((v) => (v.proximo_intento_en ? Date.parse(v.proximo_intento_en) : Date.now()));
  // Etapa 4: también los cierres de caja pendientes del usuario
  proximos.push(...(await proximosIntentosCierres()));
  if (!proximos.length) return;
  temporizador = setTimeout(() => sincronizarAhora(), Math.max(1000, Math.min(...proximos) - Date.now()));
}

// manual = true (botón «Sincronizar ahora»): ignora el backoff y las pausas por 401/403
export function sincronizarAhora({ manual = false } = {}) {
  if (!enCurso) enCurso = ciclo(manual).finally(() => { enCurso = null; });
  return enCurso;
}

// 3C.2: las sincronizadas y descartadas con más de 7 días se borran; pendientes y rechazadas NUNCA
export async function purgarCola() {
  const limite = new Date(Date.now() - SYNC.diasPurga * 86400000).toISOString();
  await db.cola_ventas.where('sincronizada_en').below(limite)
    .and((v) => v.estado === 'sincronizada' || v.estado === 'descartada').delete();
  // Etapa 4 (e): cierres enviados con más de 7 días y contadores de cajas cerradas sin ventas por enviar
  await purgarCierres();
}

export async function iniciarSincronizador() {
  if (activo) return;
  activo = true;
  // Una recarga a mitad de un envío deja ventas en «enviando»: vuelven a pendiente (el uuid evita duplicados)
  await db.cola_ventas.where('estado').equals('enviando').modify({ estado: 'pendiente' });
  await useConexion().refrescarContadoresCola();
  detenerWatch = watch(() => useConexion().modoOffline, (offline, antes) => { if (antes && !offline) sincronizarAhora(); });
  purgarCola().catch((e) => console.warn('Purga de la cola:', e));
  intervaloPurga = setInterval(() => purgarCola().catch((e) => console.warn('Purga de la cola:', e)), 3600000);
  // Etapa 3D (A): la edad de la cola avanza aunque la cola no cambie (equipo sin red toda la noche)
  intervaloEdad = setInterval(() => useConexion().refrescarContadoresCola().catch(() => {}), AVISOS.refrescoEdadMs);
  sincronizarAhora();
}

export function detenerSincronizador() {
  activo = false;
  clearTimeout(temporizador);
  clearInterval(intervaloPurga);
  clearInterval(intervaloEdad);
  if (detenerWatch) detenerWatch();
}
